import type { CommandFromServer, CommandOutput } from '../../server/src/models'
import { URL_IMAGE_DRAWING } from '../../server/src/urls'
import { connect, currentConnectionMethods } from '../shared/connect'
import { DoodleDataContext } from '../shared/data/DoodleDataContext'
import { createDoodleDocument } from '../shared/data/DoodleDocument'
import { addPdfPage } from '../shared/data/doodleDocumentToImagePdf'
import { i18n } from '../shared/i18n'
import { createPdfDocument } from '../shared/pdf'
import { downloadFile, loginUrl, noop, withProgressBar } from '../shared/utils'

const requestDownloadPdf = (doodleId: string, data: DoodleDataContext) =>
  new Promise<CommandFromServer>((resolve, reject) => {
    const connection = connect(doodleId, currentConnectionMethods, {
      onMessage: message => {
        const command = JSON.parse(message) as CommandFromServer
        switch (command.type) {
          case 'archive': {
            data.interpretCommand(command)
            const outputCommand: CommandOutput = { type: 'output', output: 'pdf' }
            connection.send(JSON.stringify(outputCommand))
            break
          }
          case 'output': {
            if (command.output === 'pdf') {
              connection.close()
              resolve(command)
            }
            break
          }
          case 'error': {
            data.interpretCommand(command)
            connection.close()
            reject(Error())
            break
          }
        }
      },
      onDisconnect: noop,
    })
  })

const fetchDoodleDocument = (doodleId: string) =>
  withProgressBar(async () => {
    try {
      const response = await fetch(URL_IMAGE_DRAWING(doodleId))
      if (!response.ok) {
        switch (response.status) {
          case 401:
          case 402:
            alert(i18n.pleaseLogin)
            location.href = loginUrl()
            return
          case 403:
            alert(i18n.permissionDenied)
            return
          case 404:
            alert(i18n.mayDeleted)
            location.href = '/'
            return
          default:
            alert(`${response.status}: ${i18n.errorOccurred}`)
            return
        }
      }
      const drawingBlob = await response.blob()
      const doodleDocument = await createDoodleDocument(drawingBlob)
      if (!doodleDocument) {
        console.error('Unsupported Content Type', drawingBlob.type)
        alert(i18n.unsupportedContentType)
        return
      }
      return doodleDocument
    } catch (error) {
      console.error(error)
      alert(i18n.errorOccurred)
    }
  })

export const downloadDoodlePdf = async (doodleId: string) => {
  const doc = await fetchDoodleDocument(doodleId)
  if (doc) {
    const data = new DoodleDataContext(doodleId, doc)
    const command = await requestDownloadPdf(doodleId, data)
    await data.interpretCommand(command)
  }
}

export const downloadConcatenatedPdf = async (doodleIds: readonly string[], filename: string) => {
  // サーバー負荷をかけたくないので並列化せず逐次実行します。
  const pdf = await createPdfDocument()
  for (const doodleId of doodleIds) {
    const doc = await fetchDoodleDocument(doodleId)
    if (!doc) {
      return
    }
    const data = new DoodleDataContext(doodleId, doc)
    await requestDownloadPdf(doodleId, data)
    for (let i = 1; i <= doc.totalPageCount; i++) {
      await addPdfPage(doodleId, pdf, doc.getPage(i), data.timestamp, data.metadata?.finalizedAt)
    }
  }
  const url = URL.createObjectURL(new Blob([(await pdf.save()) as Uint8Array<ArrayBuffer>], { type: 'application/pdf' }))
  downloadFile(filename, url)
  URL.revokeObjectURL(url)
}
